/**
 * AdminAgents.jsx
 * AI Operations Center — MeccaFlex Admin Dashboard
 *
 * Shows all 15 AI agents in real-time with invoke capability.
 * Polls every 5s when any agent is running, else every 30s.
 */

import React, {
  useState, useEffect, useRef, useCallback
} from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../contexts/ToastContext';
import api from '../../services/api';
import AdminSidebar from '../../components/AdminSidebar';
import LanguageToggle from '../../components/LanguageToggle';
import {
  RefreshCw, Play, X, ChevronDown, ChevronUp,
  Cpu, Zap, AlertTriangle, CheckCircle, Clock,
  Activity, Send, Loader, BarChart3, Terminal,
  Bot, Layers, ShieldCheck, TrendingUp, Package,
  Truck, Users, DollarSign, FileText, Globe,
  Settings, Building, FlaskConical, LayoutDashboard
} from 'lucide-react';
import '../Admin/AdminDashboard.css';
import './AdminAgents.css';

// ─── Static agent registry (fallback / initial paint) ──────────────────────
const AGENT_REGISTRY = [
  {
    id: 'orchestrator',
    name: 'Orchestrator',
    emoji: '🧠',
    division: 'intelligence',
    color: '#a855f7',
    role: 'Coordinamento strategico di tutti gli agenti e decisioni aziendali',
    icon: LayoutDashboard,
  },
  {
    id: 'quote-engine',
    name: 'Quote Engine',
    emoji: '💰',
    division: 'ops',
    color: '#f59e0b',
    role: 'Generazione automatica preventivi entro 2 ore, ottimizzazione prezzi',
    icon: FileText,
  },
  {
    id: 'order-manager',
    name: 'Order Manager',
    emoji: '📦',
    division: 'ops',
    color: '#10b981',
    role: 'Pipeline ordini: conferma → produzione → QC → consegna → pagamento',
    icon: Package,
  },
  {
    id: 'shipping-logistics',
    name: 'Shipping & Logistics',
    emoji: '🚚',
    division: 'ops',
    color: '#06b6d4',
    role: 'Coordinamento consegne, tracciamento, ottimizzazione rotte Campania',
    icon: Truck,
  },
  {
    id: 'supply-chain',
    name: 'Supply Chain',
    emoji: '🔩',
    division: 'ops',
    color: '#64748b',
    role: 'Inventario materiali, fornitori, punto di riordino, continuità produttiva',
    icon: Layers,
  },
  {
    id: 'financial-controller',
    name: 'Financial Controller',
    emoji: '📊',
    division: 'ops',
    color: '#22c55e',
    role: 'Fatturazione, cash-flow, analisi margini, conformità Regime Forfettario',
    icon: DollarSign,
  },
  {
    id: 'marketing-growth',
    name: 'Marketing & Growth',
    emoji: '📣',
    division: 'growth',
    color: '#ec4899',
    role: 'SEO, social media, campagne, acquisizione clienti mercato manifatturiero',
    icon: TrendingUp,
  },
  {
    id: 'customer-relations',
    name: 'Customer Relations',
    emoji: '🤝',
    division: 'growth',
    color: '#8b5cf6',
    role: 'CRM: nurturing, retention, fidelizzazione, outreach stagionale Campania',
    icon: Users,
  },
  {
    id: 'partnership-manager',
    name: 'Partnership Manager',
    emoji: '🌐',
    division: 'growth',
    color: '#0ea5e9',
    role: 'Reti referral, cooperative agricole, officine, reti artigianali',
    icon: Globe,
  },
  {
    id: 'content-i18n',
    name: 'Content & i18n',
    emoji: '✍️',
    division: 'growth',
    color: '#f97316',
    role: 'Contenuti bilingue IT/EN, documentazione tecnica, portfolio',
    icon: FileText,
  },
  {
    id: 'production-planner',
    name: 'Production Planner',
    emoji: '🏭',
    division: 'manufacturing',
    color: '#eab308',
    role: 'Scheduling stampa 3D, QC, ottimizzazione utilizzo stampanti',
    icon: FlaskConical,
  },
  {
    id: 'analytics',
    name: 'Analytics & BI',
    emoji: '📈',
    division: 'intelligence',
    color: '#3b82f6',
    role: 'KPI dashboard, metriche business, trend stagionali, forecasting',
    icon: BarChart3,
  },
  {
    id: 'security-compliance',
    name: 'Security & Compliance',
    emoji: '🛡️',
    division: 'platform',
    color: '#ef4444',
    role: 'GDPR, data protection, hardening piattaforma, obblighi SRLS',
    icon: ShieldCheck,
  },
  {
    id: 'devops',
    name: 'DevOps',
    emoji: '⚙️',
    division: 'platform',
    color: '#6366f1',
    role: 'CI/CD, hosting, monitoring, backup, gestione ambienti di deploy',
    icon: Settings,
  },
  {
    id: 'backend',
    name: 'Backend Engineer',
    emoji: '🔧',
    division: 'platform',
    color: '#84cc16',
    role: 'API Express.js, MongoDB, autenticazione, logica business server-side',
    icon: Terminal,
  },
];

// ─── Division config ────────────────────────────────────────────────────────
const DIVISIONS = {
  all:            { label: 'Tutti',           color: '#71717a', bg: 'rgba(113,113,122,0.12)' },
  ops:            { label: 'Operations',      color: '#10b981', bg: 'rgba(16,185,129,0.12)'  },
  growth:         { label: 'Growth',          color: '#ec4899', bg: 'rgba(236,72,153,0.12)'  },
  manufacturing:  { label: 'Manufacturing',   color: '#eab308', bg: 'rgba(234,179,8,0.12)'   },
  intelligence:   { label: 'Intelligence',    color: '#a855f7', bg: 'rgba(168,85,247,0.12)'  },
  platform:       { label: 'Platform',        color: '#6366f1', bg: 'rgba(99,102,241,0.12)'  },
};

// ─── Helper utilities ───────────────────────────────────────────────────────
function formatTimestamp(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatLatency(ms) {
  if (!ms) return '';
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
}

function timeAgo(iso) {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return 'adesso';
  if (m < 60) return `${m}m fa`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h fa`;
  return `${Math.floor(h / 24)}g fa`;
}

// Merge registry metadata onto live agent data
function mergeAgents(liveList) {
  return AGENT_REGISTRY.map((reg) => {
    const live = liveList.find((a) => a.id === reg.id) || {};
    return {
      status: 'idle',
      lastRun: null,
      lastOutput: null,
      runCount: 0,
      lastError: null,
      ...live,
      ...reg, // static overrides (id, name, emoji, etc.)
    };
  });
}

// ─── Sub-components ─────────────────────────────────────────────────────────

/** Pulsing status dot + label */
function StatusBadge({ status }) {
  const { t } = useTranslation();
  const cfg = {
    idle:    { dot: 'dot-idle',    label: t('admin.agents.statusIdle'),    icon: null },
    running: { dot: 'dot-running', label: t('admin.agents.statusRunning'), icon: <Loader size={11} className="spin-slow" /> },
    error:   { dot: 'dot-error',   label: t('admin.agents.statusError'),   icon: <AlertTriangle size={11} /> },
  }[status] || { dot: 'dot-idle', label: t('admin.agents.statusIdle'), icon: null };

  return (
    <span className={`agent-status-badge badge-${status}`}>
      <span className={`status-dot ${cfg.dot}`} />
      {cfg.icon}
      {cfg.label}
    </span>
  );
}

/** Division pill */
function DivisionBadge({ division }) {
  const d = DIVISIONS[division] || DIVISIONS.all;
  return (
    <span
      className="division-pill"
      style={{ background: d.bg, color: d.color, borderColor: d.color + '44' }}
    >
      {d.label}
    </span>
  );
}

/** Output text with expand/collapse toggle */
function OutputPreview({ text }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  if (!text) return null;

  const lines = text.split('\n');
  const needsToggle = lines.length > 3 || text.length > 240;
  const preview = needsToggle && !expanded
    ? lines.slice(0, 3).join('\n') + (lines.length > 3 ? '…' : '')
    : text;

  return (
    <div className="output-preview-block">
      <pre className="output-text">{preview}</pre>
      {needsToggle && (
        <button className="toggle-output-btn" onClick={() => setExpanded(!expanded)}>
          {expanded
            ? <><ChevronUp size={13} /> {t('admin.agents.collapse')}</>
            : <><ChevronDown size={13} /> {t('admin.agents.showAll')}</>}
        </button>
      )}
    </div>
  );
}

/** Inline invoke panel inside a card */
function InvokePanel({ agent, onResult, onClose }) {
  const { t } = useTranslation();
  const [task, setTask] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const handleSend = async () => {
    if (!task.trim()) return;
    setSending(true);
    setResult(null);
    setError(null);
    try {
      const res = await api.post(`/agents/${agent.id}/invoke`, { task: task.trim() });
      const data = res.data;
      setResult(data);
      onResult(agent.id, data);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || t('admin.agents.invoke.unknownError');
      setError(msg);
    } finally {
      setSending(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSend();
  };

  return (
    <div className="invoke-panel">
      <div className="invoke-panel-header">
        <span className="invoke-panel-title">
          <Play size={13} />
          {t('admin.agents.invoke.execute', { emoji: agent.emoji, name: agent.name })}
        </span>
        <button className="invoke-close-btn" onClick={onClose}>
          <X size={14} />
        </button>
      </div>

      <textarea
        ref={textareaRef}
        className="invoke-textarea"
        placeholder={t('admin.agents.invoke.placeholder', { name: agent.name })}
        value={task}
        onChange={(e) => setTask(e.target.value)}
        onKeyDown={handleKey}
        rows={3}
        disabled={sending}
      />

      <div className="invoke-actions">
        <button
          className="invoke-send-btn"
          onClick={handleSend}
          disabled={sending || !task.trim()}
          style={{ borderColor: agent.color, color: agent.color }}
        >
          {sending
            ? <><Loader size={14} className="spin-slow" /> {t('admin.agents.invoke.sending')}</>
            : <><Send size={14} /> {t('admin.agents.invoke.send')}</>}
        </button>
        <button className="invoke-cancel-btn" onClick={onClose} disabled={sending}>
          {t('admin.common.cancel')}
        </button>
        <span className="invoke-hint">{t('admin.agents.invoke.hint')}</span>
      </div>

      {/* Result output */}
      {(result || error) && (
        <div className={`invoke-result ${error ? 'invoke-result-error' : 'invoke-result-ok'}`}>
          {error ? (
            <>
              <div className="invoke-result-bar">
                <AlertTriangle size={13} />
                <span>{t('admin.agents.invoke.errorLabel')}</span>
              </div>
              <pre className="invoke-result-text">{error}</pre>
            </>
          ) : (
            <>
              <div className="invoke-result-bar">
                <CheckCircle size={13} />
                <span>{t('admin.agents.invoke.completed')}</span>
                {result?.latencyMs && (
                  <span className="latency-badge">{formatLatency(result.latencyMs)}</span>
                )}
                {result?.provider && (
                  <span className="provider-badge">{result.provider}</span>
                )}
              </div>
              <pre className="invoke-result-text">{result?.output || t('admin.agents.invoke.noOutput')}</pre>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** Single agent card */
function AgentCard({ agent, invoking, onInvoke, onInvokeResult, onInvokeClose }) {
  const { t } = useTranslation();
  const isInvoking = invoking[agent.id];

  return (
    <div
      className={`agent-card ${agent.status === 'error' ? 'agent-card-error' : ''} ${agent.status === 'running' ? 'agent-card-running' : ''}`}
      style={{ '--agent-color': agent.color }}
    >
      {/* Top accent bar */}
      <div className="agent-card-accent" style={{ background: agent.color }} />

      {/* Header row */}
      <div className="agent-card-header">
        <div className="agent-header-left">
          <span className="agent-emoji" role="img" aria-label={agent.name}>
            {agent.emoji}
          </span>
          <div>
            <h3 className="agent-name">{agent.name}</h3>
            <DivisionBadge division={agent.division} />
          </div>
        </div>
        <StatusBadge status={agent.status} />
      </div>

      {/* Role description */}
      <p className="agent-role">{agent.role}</p>

      {/* Stats row */}
      <div className="agent-stats-row">
        <span className="agent-stat">
          <Clock size={11} />
          {timeAgo(agent.lastRun)}
        </span>
        <span className="agent-stat">
          <Zap size={11} />
          {agent.runCount.toLocaleString('it-IT')} run
        </span>
        {agent.lastError && agent.status === 'error' && (
          <span className="agent-stat stat-error">
            <AlertTriangle size={11} />
            {agent.lastError.slice(0, 40)}…
          </span>
        )}
      </div>

      {/* Last output preview */}
      {agent.lastOutput && !isInvoking && (
        <OutputPreview text={agent.lastOutput} />
      )}

      {/* Invoke panel or button */}
      {isInvoking ? (
        <InvokePanel
          agent={agent}
          onResult={onInvokeResult}
          onClose={() => onInvokeClose(agent.id)}
        />
      ) : (
        <button
          className="agent-invoke-btn"
          style={{ '--agent-color': agent.color }}
          onClick={() => onInvoke(agent.id)}
          disabled={agent.status === 'running'}
        >
          {agent.status === 'running' ? (
            <><Loader size={13} className="spin-slow" /> {t('admin.agents.statusRunning')}</>
          ) : (
            <><Play size={13} /> {t('admin.agents.invoke.run')}</>
          )}
        </button>
      )}
    </div>
  );
}

/** Activity feed item */
function ActivityItem({ entry }) {
  const providerColor = {
    'claude-sonnet': '#8b5cf6',
    'claude-haiku':  '#06b6d4',
    'grok':          '#f59e0b',
  }[entry.provider] || '#71717a';

  return (
    <div className="activity-item">
      <div className="activity-left">
        <span className="activity-emoji">
          {AGENT_REGISTRY.find((a) => a.id === entry.agentId)?.emoji || '🤖'}
        </span>
        <div>
          <div className="activity-agent">{entry.agentId}</div>
          <div className="activity-meta">
            <span style={{ color: providerColor }}>{entry.provider}</span>
            {entry.tokens && <span className="activity-tokens">{entry.tokens.toLocaleString()} tok</span>}
          </div>
        </div>
      </div>
      <div className="activity-right">
        {entry.latencyMs && (
          <span className="activity-latency">{formatLatency(entry.latencyMs)}</span>
        )}
        <span className="activity-time">{timeAgo(entry.timestamp)}</span>
      </div>
    </div>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────
const AdminAgents = () => {
  const { t } = useTranslation();
  const toast = useToast();

  const [agents, setAgents]       = useState(() => mergeAgents([]));
  const [activity, setActivity]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [division, setDivision]   = useState('all');
  const [invoking, setInvoking]   = useState({});   // { agentId: true }
  const [lastRefresh, setLastRefresh] = useState(null);

  const pollRef = useRef(null);

  // ── Fetch agents ──────────────────────────────────────────────────────────
  const fetchAgents = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const res = await api.get('/agents');
      if (res.data?.data) {
        setAgents(mergeAgents(res.data.data));
      }
      setLastRefresh(new Date());
    } catch {
      // On failure keep existing data (merged with registry)
      if (!quiet) toast.error(t('admin.agents.errorLoad'));
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [toast]);

  // ── Fetch activity feed ───────────────────────────────────────────────────
  const fetchActivity = useCallback(async () => {
    try {
      const res = await api.get('/agents/activity');
      if (res.data?.data) setActivity(res.data.data.slice(0, 20));
    } catch {
      // silently ignore
    }
  }, []);

  // ── Smart polling (5s if any running, else 30s) ───────────────────────────
  const scheduleNextPoll = useCallback(() => {
    if (pollRef.current) clearTimeout(pollRef.current);
    const hasRunning = agents.some((a) => a.status === 'running');
    const delay = hasRunning ? 5000 : 30000;
    pollRef.current = setTimeout(async () => {
      await fetchAgents(true);
      await fetchActivity();
      scheduleNextPoll();
    }, delay);
  }, [agents, fetchAgents, fetchActivity]);

  useEffect(() => {
    fetchAgents();
    fetchActivity();
  }, []); // eslint-disable-line

  useEffect(() => {
    scheduleNextPoll();
    return () => { if (pollRef.current) clearTimeout(pollRef.current); };
  }, [scheduleNextPoll]);

  // ── Invoke handlers ───────────────────────────────────────────────────────
  const handleInvoke = (agentId) => {
    setInvoking((prev) => ({ ...prev, [agentId]: true }));
  };

  const handleInvokeResult = useCallback((agentId, result) => {
    // Optimistically update the agent card's lastOutput + runCount
    setAgents((prev) =>
      prev.map((a) =>
        a.id === agentId
          ? {
              ...a,
              lastOutput: result?.output || a.lastOutput,
              runCount: a.runCount + 1,
              lastRun: new Date().toISOString(),
              status: 'idle',
            }
          : a
      )
    );
    // Prepend to activity feed
    setActivity((prev) => [
      {
        agentId,
        provider: result?.provider || 'claude-sonnet',
        tokens: result?.tokens || null,
        latencyMs: result?.latencyMs || null,
        timestamp: new Date().toISOString(),
      },
      ...prev.slice(0, 19),
    ]);
  }, []);

  const handleInvokeClose = useCallback((agentId) => {
    setInvoking((prev) => {
      const next = { ...prev };
      delete next[agentId];
      return next;
    });
  }, []);

  // ── Derived stats ─────────────────────────────────────────────────────────
  const runningCount    = agents.filter((a) => a.status === 'running').length;
  const errorCount      = agents.filter((a) => a.status === 'error').length;
  const totalInvocations= agents.reduce((s, a) => s + (a.runCount || 0), 0);

  // ── Division counts ───────────────────────────────────────────────────────
  const divCount = Object.fromEntries(
    Object.keys(DIVISIONS).map((d) => [
      d,
      d === 'all'
        ? agents.length
        : agents.filter((a) => a.division === d).length,
    ])
  );

  // ── Filtered agents ───────────────────────────────────────────────────────
  const filteredAgents = division === 'all'
    ? agents
    : agents.filter((a) => a.division === division);

  // ── Group filtered agents by division for section labels ─────────────────
  const groupedByDivision = filteredAgents.reduce((acc, agent) => {
    acc[agent.division] = acc[agent.division] || [];
    acc[agent.division].push(agent);
    return acc;
  }, {});

  // ── Render ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="admin-layout">
        <AdminSidebar />
        <div className="admin-content">
          <div className="admin-loading">
            <div className="agents-loading-animation">
              <Bot size={48} color="#a855f7" />
              <div className="agents-loading-dots">
                <span /><span /><span />
              </div>
            </div>
            <p>{t('admin.agents.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <AdminSidebar />

      <div className="admin-content">

        {/* ── Page header ─────────────────────────────────────────────── */}
        <header className="content-header">
          <div>
            <h1>{t('admin.agents.title')}</h1>
            <p>
              {t('admin.agents.subtitle', { count: agents.length })} —{' '}
              <span className="model-tag">Claude Sonnet 4.5</span>
              {' + '}
              <span className="model-tag model-tag-haiku">Haiku fallback</span>
              {lastRefresh && (
                <span className="last-refresh-note">
                  {t('admin.agents.lastUpdated')} {timeAgo(lastRefresh)}
                </span>
              )}
            </p>
          </div>
          <div className="admin-header-right">
            <LanguageToggle />
            <button
              className="admin-btn admin-btn-secondary refresh-btn"
              onClick={() => { fetchAgents(); fetchActivity(); }}
            >
              <RefreshCw size={16} />
              {t('admin.common.refresh')}
            </button>
          </div>
        </header>

        {/* ── Stats bar ───────────────────────────────────────────────── */}
        <div className="agents-stats-bar">
          <div className="agents-stat-tile">
            <div className="agents-stat-icon" style={{ background: 'rgba(168,85,247,0.15)' }}>
              <Bot size={20} color="#a855f7" />
            </div>
            <div>
              <div className="agents-stat-value">{agents.length}</div>
              <div className="agents-stat-label">{t('admin.agents.stats.total')}</div>
            </div>
          </div>

          <div className="agents-stat-tile">
            <div className="agents-stat-icon" style={{ background: 'rgba(16,185,129,0.15)' }}>
              <Activity size={20} color="#10b981" />
            </div>
            <div>
              <div className="agents-stat-value" style={{ color: runningCount > 0 ? '#10b981' : undefined }}>
                {runningCount}
              </div>
              <div className="agents-stat-label">{t('admin.agents.stats.running')}</div>
            </div>
          </div>

          <div className="agents-stat-tile">
            <div className="agents-stat-icon" style={{ background: 'rgba(99,102,241,0.15)' }}>
              <Zap size={20} color="#6366f1" />
            </div>
            <div>
              <div className="agents-stat-value">{totalInvocations.toLocaleString('it-IT')}</div>
              <div className="agents-stat-label">{t('admin.agents.stats.invocations')}</div>
            </div>
          </div>

          <div className="agents-stat-tile">
            <div className="agents-stat-icon" style={{ background: errorCount > 0 ? 'rgba(239,68,68,0.15)' : 'rgba(113,113,122,0.12)' }}>
              <AlertTriangle size={20} color={errorCount > 0 ? '#ef4444' : '#71717a'} />
            </div>
            <div>
              <div className="agents-stat-value" style={{ color: errorCount > 0 ? '#ef4444' : undefined }}>
                {errorCount}
              </div>
              <div className="agents-stat-label">{t('admin.agents.stats.errors')}</div>
            </div>
          </div>

          <div className="agents-stat-tile">
            <div className="agents-stat-icon" style={{ background: 'rgba(6,182,212,0.15)' }}>
              <Cpu size={20} color="#06b6d4" />
            </div>
            <div>
              <div className="agents-stat-value">{Object.keys(DIVISIONS).length - 1}</div>
              <div className="agents-stat-label">{t('admin.agents.stats.divisions')}</div>
            </div>
          </div>
        </div>

        {/* ── Main content: Cards + Activity sidebar ──────────────────── */}
        <div className="agents-main-layout">

          {/* LEFT: division tabs + grid */}
          <div className="agents-left-col">

            {/* Division filter tabs */}
            <div className="division-tabs">
              {Object.entries(DIVISIONS).map(([key, cfg]) => (
                <button
                  key={key}
                  className={`division-tab ${division === key ? 'division-tab-active' : ''}`}
                  style={division === key
                    ? { borderColor: cfg.color, color: cfg.color, background: cfg.bg }
                    : {}}
                  onClick={() => setDivision(key)}
                >
                  {t('admin.agents.divisions.' + key)}
                  <span
                    className="division-count"
                    style={division === key ? { background: cfg.color, color: '#000' } : {}}
                  >
                    {divCount[key]}
                  </span>
                </button>
              ))}
            </div>

            {/* Agent grid — grouped by division when viewing "All" */}
            {division === 'all' ? (
              Object.entries(groupedByDivision).map(([div, divAgents]) => {
                const d = DIVISIONS[div] || {};
                return (
                  <div key={div} className="division-section">
                    <div
                      className="division-section-label"
                      style={{ borderColor: d.color + '60', color: d.color }}
                    >
                      <span className="division-section-dot" style={{ background: d.color }} />
                      {t('admin.agents.divisions.' + div)}
                      <span className="division-section-count">{t('admin.agents.divisionCount', { count: divAgents.length })}</span>
                    </div>
                    <div className="agents-grid">
                      {divAgents.map((agent) => (
                        <AgentCard
                          key={agent.id}
                          agent={agent}
                          invoking={invoking}
                          onInvoke={handleInvoke}
                          onInvokeResult={handleInvokeResult}
                          onInvokeClose={handleInvokeClose}
                        />
                      ))}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="agents-grid">
                {filteredAgents.map((agent) => (
                  <AgentCard
                    key={agent.id}
                    agent={agent}
                    invoking={invoking}
                    onInvoke={handleInvoke}
                    onInvokeResult={handleInvokeResult}
                    onInvokeClose={handleInvokeClose}
                  />
                ))}
              </div>
            )}

            {filteredAgents.length === 0 && (
              <div className="admin-empty">
                <Bot size={40} color="#3f3f46" />
                <p>{t('admin.agents.emptyDivision')}</p>
              </div>
            )}
          </div>

          {/* RIGHT: Activity feed */}
          <div className="agents-right-col">
            <div className="activity-panel">
              <div className="activity-panel-header">
                <span className="activity-panel-title">
                  <Terminal size={14} />
                  {t('admin.agents.activity.title')}
                </span>
                <span className="activity-panel-badge">{activity.length}</span>
              </div>

              {/* Live indicator */}
              <div className="activity-live-row">
                <span className="live-dot" />
                <span className="live-label">{t('admin.agents.activity.live')}</span>
              </div>

              <div className="activity-feed">
                {activity.length === 0 ? (
                  <div className="activity-empty">
                    <Clock size={24} color="#3f3f46" />
                    <p>{t('admin.agents.activity.empty')}</p>
                  </div>
                ) : (
                  activity.map((entry, i) => (
                    <ActivityItem key={`${entry.agentId}-${entry.timestamp}-${i}`} entry={entry} />
                  ))
                )}
              </div>

              {/* Legend */}
              <div className="activity-legend">
                <span className="legend-item" style={{ color: '#8b5cf6' }}>● Sonnet</span>
                <span className="legend-item" style={{ color: '#06b6d4' }}>● Haiku</span>
                <span className="legend-item" style={{ color: '#f59e0b' }}>● Grok</span>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AdminAgents;
